'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { WatchlistItem, useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { formatTMDBDate } from '@/lib/utils';
import { isEpisodeAvailable, seasonHasUnairedEpisodes, seasonHasDisplayableData } from '@/lib/episodeAvailability';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  X, ChevronLeft, ChevronRight, Tv, Film, Star, PlayCircle,
  CheckCircle2, Circle, Check, Calendar, Clock, ExternalLink,
  ChevronDown, Lock, Sparkles, Users, Play, Trash2, MoreVertical
} from 'lucide-react';
import { format } from 'date-fns';
import { ratingsService } from '@/lib/ratings';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { WatchTogetherSection } from '@/components/WatchTogetherSection';

interface SeasonDetail {
  season_number: number;
  episode_count: number;
  poster_path: string | null;
  name: string;
  air_date: string | null;
  episodes?: Array<{
    air_date: string | null;
    episode_number: number;
    name: string;
  }>;
}

interface CastMember {
  id: number;
  name: string;
  character: string;
  profile_path: string | null;
}

interface Video {
  id: string;
  key: string;
  name: string;
  type: string;
  site: string;
}

interface WatchProvider {
  provider_id: number;
  provider_name: string;
  logo_path: string;
}

interface ItemDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: number | null;
  itemType: 'tv' | 'movie' | null;
  onNavigateNext: () => void;
  onNavigatePrev: () => void;
  canNavigateNext: boolean;
  canNavigatePrev: boolean;
  watchlist: WatchlistItem[];
}

// Genre mapping
const GENRE_MAP: Record<number, string> = {
  28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy', 80: 'Crime',
  99: 'Documentary', 18: 'Drama', 10751: 'Family', 14: 'Fantasy', 36: 'History',
  27: 'Horror', 10402: 'Music', 9648: 'Mystery', 10749: 'Romance', 878: 'Sci-Fi',
  10770: 'TV Movie', 53: 'Thriller', 10752: 'War', 37: 'Western',
  10759: 'Action & Adventure', 10762: 'Kids', 10763: 'News', 10764: 'Reality',
  10765: 'Sci-Fi & Fantasy', 10766: 'Soap', 10767: 'Talk', 10768: 'War & Politics'
};

export function ItemDetailDialog({
  isOpen,
  onClose,
  itemId,
  itemType,
  onNavigateNext,
  onNavigatePrev,
  canNavigateNext,
  canNavigatePrev,
  watchlist
}: ItemDetailDialogProps) {
  const router = useRouter();
  const { user } = useAuth();
  const {
    markSeasonWatched, markSeasonUnwatched,
    markEpisodeWatched, markEpisodeUnwatched,
    toggleCurrentlyWatching, toggleMovieWatched,
    removeFromWatchlist, setPersonalRank, updateWatchlistItem
  } = useStore();

  // Get item from watchlist
  const item = watchlist.find(i => i.id === itemId);

  // State
  const [seasons, setSeasons] = useState<SeasonDetail[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [expandedSeasons, setExpandedSeasons] = useState<Set<number>>(new Set());
  const [cast, setCast] = useState<CastMember[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [providers, setProviders] = useState<WatchProvider[]>([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [showRatingPicker, setShowRatingPicker] = useState(false);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());

  // Mobile detection (shared SSR-safe hook)
  const isMobile = useIsMobile();

  // Unified touch-gesture state for mobile.
  // A single gesture is locked to one axis ('x' = navigate, 'y' = drag-to-close)
  // after the first significant movement, so a vertical scroll with slight
  // sideways thumb drift never triggers navigation, and vice-versa.
  const [dragOffset, setDragOffset] = useState(0);   // vertical close offset (px)
  const [swipeOffset, setSwipeOffset] = useState(0);  // horizontal nav offset (px)
  const [isDragging, setIsDragging] = useState(false); // axis locked to 'y'
  const [isSwiping, setIsSwiping] = useState(false);   // axis locked to 'x'
  const gestureRef = useRef<{
    startX: number;
    startY: number;
    axis: 'x' | 'y' | null;
    atTop: boolean; // content was scrolled to top when the gesture began
    startOnHero: boolean; // touch started on the pinned hero — always closes on swipe-down
  } | null>(null);

  // Navigation animation state
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lastNavDirection, setLastNavDirection] = useState<'left' | 'right' | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  // Track navigation-animation timers so we can cancel them on close/unmount and
  // before starting a new navigation — prevents leaks and "setState after unmount".
  const animTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearAnimTimers = useCallback(() => {
    animTimersRef.current.forEach(clearTimeout);
    animTimersRef.current = [];
  }, []);

  // Clear any pending animation timers on unmount.
  useEffect(() => clearAnimTimers, [clearAnimTimers]);

  // Prevent body scroll when dialog is open and reset animation state
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      // Reset animation state when dialog closes
      clearAnimTimers();
      setLastNavDirection(null);
      setSlideDirection(null);
      setIsAnimating(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, clearAnimTimers]);

  // Animated navigation handlers
  const animateAndNavigate = useCallback((direction: 'left' | 'right', navigateFn: () => void) => {
    if (isAnimating) return;
    clearAnimTimers();
    setIsAnimating(true);
    setSlideDirection(direction);
    setLastNavDirection(direction);

    // After slide-out animation, trigger navigation
    animTimersRef.current.push(setTimeout(() => {
      navigateFn();
      setSlideDirection(null);
      // Reset animation state after a brief delay to allow incoming animation
      animTimersRef.current.push(setTimeout(() => {
        setIsAnimating(false);
      }, 300));
    }, 200)); // Duration of slide-out
  }, [isAnimating, clearAnimTimers]);

  const handleAnimatedNext = useCallback(() => {
    if (canNavigateNext && !isAnimating) {
      animateAndNavigate('left', onNavigateNext);
    }
  }, [canNavigateNext, isAnimating, animateAndNavigate, onNavigateNext]);

  const handleAnimatedPrev = useCallback(() => {
    if (canNavigatePrev && !isAnimating) {
      animateAndNavigate('right', onNavigatePrev);
    }
  }, [canNavigatePrev, isAnimating, animateAndNavigate, onNavigatePrev]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && canNavigatePrev) {
        handleAnimatedPrev();
      } else if (e.key === 'ArrowRight' && canNavigateNext) {
        handleAnimatedNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canNavigateNext, canNavigatePrev, onClose, handleAnimatedNext, handleAnimatedPrev]);

  // Load data when item changes
  useEffect(() => {
    if (!isOpen || !itemId || !itemType) return;

    // Reset state
    setSeasons([]);
    setExpandedSeasons(new Set());
    setCast([]);
    setVideos([]);
    setProviders([]);
    setFailedThumbnails(new Set());

    // Load seasons for TV shows
    if (itemType === 'tv') {
      loadSeasonDetails();
    }

    // Load extra data (cast, videos, providers)
    loadExtraData();
  }, [isOpen, itemId, itemType]);

  const loadSeasonDetails = async () => {
    if (!itemId) return;
    setLoadingSeasons(true);
    try {
      const showDetails = await tmdb.getShowWithDetails(itemId);
      if (showDetails.seasons) {
        const regularSeasons = showDetails.seasons.filter((s: any) => s.season_number > 0);
        const seasonsWithEpisodes = await Promise.all(
          regularSeasons.map(async (season: any) => {
            try {
              const seasonDetail = await tmdb.getSeason(itemId, season.season_number);
              return { ...season, episodes: seasonDetail.episodes || [] };
            } catch {
              return season;
            }
          })
        );
        setSeasons(seasonsWithEpisodes);

        // Self-heal the stored season count: it may still include phantom
        // 0/0 seasons until the show's next scheduled content check
        const displayable = seasonsWithEpisodes.filter(s => seasonHasDisplayableData(s)).length;
        if (displayable > 0 && item && item.totalSeasons !== displayable) {
          updateWatchlistItem(item.id, { totalSeasons: displayable });
        }
      }
    } catch (error) {
      console.error('Error loading seasons:', error);
    } finally {
      setLoadingSeasons(false);
    }
  };

  const loadExtraData = async () => {
    if (!itemId || !itemType) return;
    setLoadingExtras(true);
    try {
      const [credits, videosData, providersData] = await Promise.all([
        itemType === 'tv' ? tmdb.getShowCredits(itemId) : tmdb.getMovieCredits(itemId),
        itemType === 'tv' ? tmdb.getShowVideos(itemId) : tmdb.getMovieVideos(itemId),
        itemType === 'tv' ? tmdb.getShowWatchProviders(itemId) : tmdb.getMovieWatchProviders(itemId),
      ]);

      // Process cast
      if (credits?.cast) {
        setCast(credits.cast.slice(0, 8));
      }

      // Process videos - filter to trailers/teasers and validate thumbnails
      if (videosData?.results) {
        const filtered = videosData.results.filter(
          (v: Video) => v.site === 'YouTube' && ['Trailer', 'Teaser'].includes(v.type)
        );

        // Validate thumbnails by checking if maxresdefault exists (returns 404 for unavailable videos)
        const validatedVideos = await Promise.all(
          filtered.slice(0, 4).map(async (video: Video) => {
            try {
              const response = await fetch(`https://img.youtube.com/vi/${video.key}/maxresdefault.jpg`, { method: 'HEAD' });
              return response.ok ? video : null;
            } catch {
              return null;
            }
          })
        );

        setVideos(validatedVideos.filter((v): v is Video => v !== null).slice(0, 2));
      }

      // Process watch providers
      if (providersData?.results?.US?.flatrate) {
        setProviders(providersData.results.US.flatrate.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading extra data:', error);
    } finally {
      setLoadingExtras(false);
    }
  };

  // Handlers — guards live here (not only at render) so no code path can
  // mark unaired episodes as watched
  const handleSeasonToggle = async (seasonNumber: number) => {
    if (!item) return;
    const isWatched = item.watchedSeasons.includes(seasonNumber);
    if (isWatched) {
      await markSeasonUnwatched(item.id, seasonNumber);
    } else {
      const season = seasons.find(s => s.season_number === seasonNumber);
      if (seasonHasUnairedEpisodes(season?.episodes)) {
        return;
      }
      await markSeasonWatched(item.id, seasonNumber);
    }
  };

  const handleEpisodeToggle = async (seasonNumber: number, episodeNumber: number) => {
    if (!item) return;
    const isWatched = item.watchedEpisodes?.[seasonNumber]?.includes(episodeNumber);
    if (isWatched) {
      await markEpisodeUnwatched(item.id, seasonNumber, episodeNumber);
    } else {
      const season = seasons.find(s => s.season_number === seasonNumber);
      const episode = season?.episodes?.find((ep: any) => ep.episode_number === episodeNumber);
      if (episode && !isEpisodeAvailable(episode, season?.episodes || [])) {
        return;
      }
      await markEpisodeWatched(item.id, seasonNumber, episodeNumber);
      // Check if season is complete
      const totalEpisodes = season?.episodes?.length || 0;
      const newWatchedCount = (item.watchedEpisodes?.[seasonNumber]?.length || 0) + 1;
      if (newWatchedCount >= totalEpisodes) {
        await markSeasonWatched(item.id, seasonNumber);
      }
    }
  };

  const handleStarClick = async (starIndex: number) => {
    if (!item || !user) return;
    const value = (starIndex + 1) * 2;
    await ratingsService.setUserRating(user.uid, item.id, item.type, value, {
      mediaTitle: item.name,
      posterPath: item.posterPath || undefined
    });
    await setPersonalRank(item.id, value);
    setHoverRating(null);
  };

  // Movement (px) before we commit a gesture to one axis.
  const AXIS_LOCK_THRESHOLD = 12;

  // Unified touch handlers on the sheet container.
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    // Did the touch land on the pinned hero (data-drag-zone="hero")?
    // Swipe-down on the hero closes the dialog from anywhere — no need to
    // scroll the body back to the top first.
    const target = e.target as HTMLElement | null;
    const startOnHero = !!target?.closest?.('[data-drag-zone="hero"]');
    gestureRef.current = {
      startX: t.clientX,
      startY: t.clientY,
      axis: null,
      atTop: (contentRef.current?.scrollTop ?? 0) <= 0,
      startOnHero,
    };
    setDragOffset(0);
    setSwipeOffset(0);
    setIsDragging(false);
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const g = gestureRef.current;
    if (!g) return;
    const t = e.touches[0];
    const dx = t.clientX - g.startX;
    const dy = t.clientY - g.startY;

    // Decide the axis once movement is meaningful, then stick with it.
    if (g.axis === null) {
      if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal → navigation.
        g.axis = 'x';
        setIsSwiping(true);
      } else if ((g.startOnHero || g.atTop) && dy > 0) {
        // Vertical downward AND either the touch started on the pinned hero
        // (the hero acts as a "drag handle" from anywhere in the menu) OR the
        // body is already scrolled to the top → drag-to-close.
        g.axis = 'y';
        setIsDragging(true);
      } else {
        // Vertical scroll within content for now — but we may "upgrade" to
        // drag-to-close below if the user scrolls back to the top and keeps pulling.
        g.axis = 'y';
      }
    }

    if (g.axis === 'x' && isSwiping) {
      setSwipeOffset(dx);
    } else if (g.axis === 'y') {
      // If we're already in drag-to-close mode, keep dragging the sheet.
      if (isDragging) {
        const offset = Math.max(0, dy);
        if (offset > 0) e.preventDefault();
        setDragOffset(offset);
        return;
      }
      // Otherwise, this gesture started as a content scroll. Upgrade it to
      // drag-to-close the moment the content is scrolled to the top AND the
      // user is still pulling downward — so swiping down anywhere closes the
      // sheet once everything is at the top.
      if ((contentRef.current?.scrollTop ?? 0) <= 0 && dy > 0) {
        // Re-anchor the gesture so dragOffset starts from this point, not from
        // the original touch position (avoids the sheet jumping).
        g.startY = t.clientY;
        g.atTop = true;
        setIsDragging(true);
        e.preventDefault();
        setDragOffset(0);
      }
    }
  };

  const handleTouchEnd = () => {
    const g = gestureRef.current;
    gestureRef.current = null;

    if (g?.axis === 'y' && isDragging) {
      // Close when dragged down ~18% of viewport height (proportional across phones).
      const closeThreshold = Math.max(80, window.innerHeight * 0.18);
      if (dragOffset > closeThreshold) {
        onClose();
      }
    } else if (g?.axis === 'x' && isSwiping) {
      // Navigate when swiped ~22% of viewport width.
      const swipeThreshold = Math.max(60, window.innerWidth * 0.22);
      if (Math.abs(swipeOffset) > swipeThreshold) {
        if (swipeOffset > 0 && canNavigatePrev) {
          handleAnimatedPrev();
        } else if (swipeOffset < 0 && canNavigateNext) {
          handleAnimatedNext();
        }
      }
    }

    // Reset gesture offsets/flags regardless of axis.
    setDragOffset(0);
    setSwipeOffset(0);
    setIsDragging(false);
    setIsSwiping(false);
  };

  // Get status text
  const getStatusText = () => {
    if (!item?.status) return null;
    if (item.status === 'Returning Series') {
      return item.nextEpisode ? 'Active' : 'Returning';
    }
    return item.status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-600';
      case 'Returning': return 'bg-blue-600';
      case 'Ended': return 'bg-gray-600';
      case 'Cancelled': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  if (!isOpen || !item) return null;

  // Prefer the loaded season list (with phantom 0/0 seasons filtered out)
  // over the stored count, which may still include placeholder seasons
  const displayableSeasonCount = seasons.length > 0
    ? seasons.filter(seasonHasDisplayableData).length
    : item.totalSeasons || 0;

  const watchedPercentage = item.type === 'tv' && displayableSeasonCount
    ? Math.min((item.watchedSeasons.length / displayableSeasonCount) * 100, 100)
    : 0;

  const genreNames = (item.genres || []).map(id => GENRE_MAP[id]).filter(Boolean).slice(0, 4);

  // Render content
  const renderContent = () => (
    <>
      {/* Cinematic hero — sized to match the movie/tv page proportions.
          shrink-0 pins the hero in the dialog's flex-col layout so it can't be
          compressed by the body content (the bottom-anchored title block would
          otherwise overflow the box and get clipped — desktop-only symptom).
          data-drag-zone="hero" makes the hero a "drag handle": swipe-down on
          it closes the sheet from anywhere in the menu, no matter where the
          body is scrolled. */}
      <div data-drag-zone="hero" className="relative shrink-0 h-[26vh] md:h-[28vh] min-h-[240px] md:max-h-[280px] overflow-hidden">
        {/* Backdrop — full-bleed, extends to top of sheet */}
        {item.backdropPath ? (
          <Image
            src={tmdb.getImageUrl(item.backdropPath, 'original')}
            alt={item.name}
            fill
            sizes="(max-width: 768px) 100vw, 768px"
            className="object-cover object-top opacity-90"
            priority
          />
        ) : item.posterPath ? (
          <Image
            src={tmdb.getImageUrl(item.posterPath, 'w500')}
            alt={item.name}
            fill
            sizes="(max-width: 768px) 100vw, 768px"
            className="object-cover blur-2xl opacity-40"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
        )}
        {/* Cinematic layered gradients (same as movie page) */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
        <div className="absolute bottom-0 inset-x-0 h-64 bg-gradient-to-t from-black via-black/95 to-transparent" />
        <div className="absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-full bg-red-600/25 blur-[140px] pointer-events-none" />

        {/* Content — vertically centered with equal top/bottom breathing room on all sizes */}
        <div className="absolute inset-0 flex items-center px-4 py-5 md:px-6 md:py-6">
          <div className="flex items-end gap-4 w-full">
            {/* Poster — same proportions as the movie page mobile poster */}
            <div className="flex-shrink-0 w-28 sm:w-32 md:w-40 aspect-[2/3] rounded-xl md:rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl">
              {item.posterPath ? (
                <Image
                  src={tmdb.getImageUrl(item.posterPath, 'w500')}
                  alt={item.name}
                  width={160}
                  height={240}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  {item.type === 'tv' ? <Tv className="h-8 w-8 text-gray-600" /> : <Film className="h-8 w-8 text-gray-600" />}
                </div>
              )}
            </div>

            {/* Title block */}
            <div className="flex-1 min-w-0">
              {/* Genres */}
              {genreNames.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-2">
                  {genreNames.slice(0, 3).map(genre => (
                    <span key={genre} className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">
                      {genre}
                    </span>
                  ))}
                </div>
              )}

              {/* Title — bigger to match the movie/tv page */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white leading-[1.05] drop-shadow-2xl line-clamp-3 mb-2">
                {item.name}
              </h2>

              {/* Meta chips */}
              <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto scrollbar-hide">
                {item.rating ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 ring-1 ring-white/15 px-2.5 py-1 text-xs text-white backdrop-blur-sm whitespace-nowrap">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                    {item.rating.toFixed(1)}
                  </span>
                ) : null}
                {getStatusText() && (
                  <span className="inline-flex items-center rounded-full bg-white/10 ring-1 ring-white/15 px-2.5 py-1 text-xs text-gray-200 backdrop-blur-sm whitespace-nowrap">
                    {getStatusText()}
                  </span>
                )}
                {item.type === 'tv' && displayableSeasonCount ? (
                  <span className="inline-flex items-center rounded-full bg-white/10 ring-1 ring-white/15 px-2.5 py-1 text-xs text-gray-200 backdrop-blur-sm whitespace-nowrap">
                    {displayableSeasonCount} {displayableSeasonCount === 1 ? 'Season' : 'Seasons'}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons — cinematic glass pills */}
      <div className="px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          {/* Watching / Watched primary toggle */}
          {item.type === 'tv' ? (
            <button
              onClick={() => toggleCurrentlyWatching(item.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                item.currentlyWatching
                  ? 'bg-white text-black hover:bg-gray-200'
                  : 'bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/20 backdrop-blur-md'
              }`}
            >
              <PlayCircle className="h-4 w-4" />
              {item.currentlyWatching ? 'Watching' : 'Watch'}
            </button>
          ) : (
            <button
              onClick={() => toggleMovieWatched(item.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                item.watched
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-white text-black hover:bg-gray-200'
              }`}
            >
              <CheckCircle2 className="h-4 w-4" />
              {item.watched ? 'Watched' : 'Mark Watched'}
            </button>
          )}

          {/* Rate */}
          <button
            onClick={() => setShowRatingPicker(true)}
            className={`flex items-center justify-center gap-1.5 rounded-full bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/20 backdrop-blur-md px-4 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
              item.personalRank ? 'ring-yellow-500/60' : ''
            }`}
          >
            <Star className={`h-4 w-4 ${item.personalRank ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            {item.personalRank ? `${item.personalRank / 2}/5` : 'Rate'}
          </button>

          {/* View full page */}
          <button
            onClick={() => {
              onClose();
              router.push(`/${item.type}/${item.id}`);
            }}
            className="flex items-center justify-center gap-1.5 rounded-full bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/20 backdrop-blur-md px-4 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="hidden sm:inline">View Page</span>
          </button>
        </div>

        {/* Quick Complete Button for TV shows - Full width on mobile */}
        {item.type === 'tv' && (() => {
          for (const season of seasons) {
            if (item.watchedSeasons.includes(season.season_number)) continue;
            if (!season.episodes) continue;
            for (const ep of season.episodes) {
              if (!isEpisodeAvailable(ep, season.episodes)) continue;
              if (!item.watchedEpisodes?.[season.season_number]?.includes(ep.episode_number)) {
                return (
                  <button
                    onClick={() => handleEpisodeToggle(season.season_number, ep.episode_number)}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-semibold transition-all shadow-lg shadow-red-500/20 hover:shadow-red-500/30 active:scale-[0.98]"
                    title={`Mark S${season.season_number}E${ep.episode_number} as watched`}
                  >
                    <Check className="h-5 w-5" />
                    <span>Complete S{season.season_number}E{ep.episode_number}</span>
                  </button>
                );
              }
            }
          }
          return null;
        })()}
      </div>

      {/* Scrollable content area. Touch gestures are handled by the sheet
          container; this just provides the scroll position via contentRef.
          overscroll-contain stops overscroll from chaining to the page behind. */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto overscroll-contain"
      >
        {/* Season Tracker (TV only) - Directly visible */}
        {item.type === 'tv' && (
          <div className="px-4 py-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base md:text-lg font-semibold tracking-tight text-white flex items-center gap-2">
                <Tv className="h-5 w-5 text-red-500" />
                Season Tracker
              </h3>
              <span className="text-sm text-gray-400">
                {item.watchedSeasons.length}/{displayableSeasonCount} seasons
              </span>
            </div>

            {/* Overall progress — bright logo red */}
            <div className="mb-4">
              <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-600 transition-all"
                  style={{ width: `${watchedPercentage}%` }}
                />
              </div>
            </div>

            {/* Season list */}
            {loadingSeasons ? (
              <div className="text-center py-8">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-red-600 border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-3">
                {seasons.filter(seasonHasDisplayableData).map((season) => {
                  const isWatched = item.watchedSeasons.includes(season.season_number);
                  const watchedEpisodeCount = item.watchedEpisodes?.[season.season_number]?.length || 0;
                  const totalEpisodes = season.episodes?.length || season.episode_count || 0;
                  const isExpanded = expandedSeasons.has(season.season_number);
                  const episodeProgress = totalEpisodes > 0 ? (watchedEpisodeCount / totalEpisodes) * 100 : 0;

                  // Check if season has unaired episodes (future-dated, or
                  // undated with nothing aired after them)
                  const hasFutureEpisodes = seasonHasUnairedEpisodes(season.episodes);

                  // Year the season premiered (first episode's release)
                  const seasonYear = (
                    season.air_date ||
                    season.episodes?.find((ep: any) => ep.air_date)?.air_date ||
                    ''
                  ).slice(0, 4);

                  // "In progress" = some episodes watched but season not complete
                  // and the season isn't gated by future episodes.
                  const isInProgress = !isWatched && watchedEpisodeCount > 0 && !hasFutureEpisodes;

                  return (
                    <div
                      key={season.season_number}
                      className={`rounded-2xl overflow-hidden transition-all ${
                        isWatched
                          ? 'bg-green-950/30 ring-1 ring-green-500/40'
                          : isInProgress
                            ? 'bg-red-950/30 ring-1 ring-red-500/40'
                            : 'bg-white/5 ring-1 ring-white/10'
                      }`}
                    >
                      {/* Season header — click anywhere to expand/collapse episodes */}
                      <div
                        className={`p-3 flex items-center gap-3 ${
                          season.episodes && season.episodes.length > 0 ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => {
                          if (!season.episodes || season.episodes.length === 0) return;
                          const newExpanded = new Set(expandedSeasons);
                          if (isExpanded) {
                            newExpanded.delete(season.season_number);
                          } else {
                            newExpanded.add(season.season_number);
                          }
                          setExpandedSeasons(newExpanded);
                        }}
                      >
                        {/* Checkbox (toggles watched state — stop propagation so it doesn't expand) */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSeasonToggle(season.season_number);
                          }}
                          disabled={!isWatched && hasFutureEpisodes}
                          className="flex-shrink-0"
                        >
                          {isWatched ? (
                            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                              <Check className="h-5 w-5 text-white" />
                            </div>
                          ) : isInProgress ? (
                            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_12px_-2px_rgba(220,38,38,0.7)]" title="Currently watching">
                              <PlayCircle className="h-5 w-5 text-white" />
                            </div>
                          ) : hasFutureEpisodes ? (
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                              <Lock className="h-4 w-4 text-gray-500" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full border-2 border-gray-600 flex items-center justify-center hover:border-gray-500">
                              <Circle className="h-4 w-4 text-gray-600" />
                            </div>
                          )}
                        </button>

                        {/* Season info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-white text-sm">
                              {season.name}
                              {seasonYear && <span className="ml-2 text-xs font-normal text-gray-500">{seasonYear}</span>}
                            </span>
                            <span className="text-xs text-gray-400">{watchedEpisodeCount}/{totalEpisodes}</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded-full mt-1.5 overflow-hidden">
                            <div
                              className={`h-full transition-all ${isWatched ? 'bg-green-500' : 'bg-red-500'}`}
                              style={{ width: `${episodeProgress}%` }}
                            />
                          </div>
                        </div>

                        {/* Expand indicator (the whole header is clickable) */}
                        {season.episodes && season.episodes.length > 0 && (
                          <div className="p-1.5 flex-shrink-0">
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        )}
                      </div>

                      {/* Episode list */}
                      {isExpanded && season.episodes && (
                        <div className="px-3 pb-3 space-y-1.5">
                          {season.episodes.map((episode: any) => {
                            const isEpWatched = item.watchedEpisodes?.[season.season_number]?.includes(episode.episode_number);
                            const isUnavailable = !isEpisodeAvailable(episode, season.episodes || []);

                            return (
                              <button
                                key={episode.episode_number}
                                onClick={() => (!isUnavailable || isEpWatched) && handleEpisodeToggle(season.season_number, episode.episode_number)}
                                disabled={isUnavailable && !isEpWatched}
                                className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-all ${
                                  isEpWatched
                                    ? 'bg-green-900/30'
                                    : isUnavailable
                                    ? 'bg-gray-800/30 opacity-50'
                                    : 'bg-gray-800/30 hover:bg-gray-700/30'
                                }`}
                              >
                                {/* Episode number */}
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                  isEpWatched ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'
                                }`}>
                                  {episode.episode_number}
                                </div>

                                {/* Episode name */}
                                <span className={`flex-1 text-sm truncate ${isEpWatched ? 'text-white' : 'text-gray-300'}`}>
                                  {episode.name}
                                </span>

                                {/* Release date and status */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {episode.air_date && (
                                    <span className="text-xs text-gray-500">
                                      {formatTMDBDate(episode.air_date, 'MMM d, yyyy')}
                                    </span>
                                  )}
                                  {isEpWatched ? (
                                    <Check className="h-4 w-4 text-green-400" />
                                  ) : isUnavailable ? (
                                    <Lock className="h-3 w-3 text-gray-500" />
                                  ) : (
                                    <div className="w-4 h-4" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>
            )}
          </div>
        )}

        {/* Watch Together — per-show progress sharing (TV only) */}
        {item.type === 'tv' && <WatchTogetherSection item={item} />}

        {/* Overview */}
        {item.overview && (
          <div className="px-4 py-5 border-b border-white/10">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Overview</h3>
            <p className="text-gray-200/90 text-sm leading-relaxed">{item.overview}</p>
          </div>
        )}

        {/* Where to Watch — cinematic glass pills (same as movie page) */}
        {providers.length > 0 && (
          <div className="px-4 py-5 border-b border-white/10">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Where to Watch</h3>
            <div className="flex flex-wrap gap-2.5">
              {providers.map(provider => (
                <div
                  key={provider.provider_id}
                  className="flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/15 backdrop-blur-sm px-4 py-2"
                >
                  <Image
                    src={tmdb.getImageUrl(provider.logo_path, 'w92')}
                    alt={provider.provider_name}
                    width={24}
                    height={24}
                    className="rounded"
                  />
                  <span className="text-white text-sm font-medium">{provider.provider_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trailer — cinematic card with ring + red glow on hover */}
        {videos.length > 0 && (
          <div className="px-4 py-5 border-b border-white/10">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Trailer</h3>
            <div className="grid gap-3">
              {videos.map(video => (
                <a
                  key={video.id}
                  href={`https://www.youtube.com/watch?v=${video.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative aspect-video rounded-2xl overflow-hidden ring-1 ring-white/10 group transition-all hover:ring-red-500/50 hover:shadow-[0_0_30px_-5px_rgba(220,38,38,0.45)]"
                >
                  <Image
                    src={`https://img.youtube.com/vi/${video.key}/maxresdefault.jpg`}
                    alt={video.name}
                    fill
                    sizes="(max-width: 768px) 100vw, 700px"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    unoptimized
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="rounded-full bg-black/70 backdrop-blur-sm ring-1 ring-white/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-red-400">
                      {video.type}
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
                      <Play className="h-7 w-7 text-white ml-1" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Cast — cinematic poster cards (same as movie page) */}
        {cast.length > 0 && (
          <div className="px-4 py-5 border-b border-white/10">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Cast</h3>
            <div className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-row -mt-1 pt-1 pb-2">
              {cast.map(member => (
                <Link
                  key={member.id}
                  href={`/person/${member.id}`}
                  onClick={onClose}
                  className="group flex-shrink-0 w-24 sm:w-28"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden ring-1 ring-white/10 transition-all duration-300 group-hover:ring-red-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(220,38,38,0.45)] group-hover:-translate-y-1">
                    {member.profile_path ? (
                      <Image
                        src={tmdb.getImageUrl(member.profile_path, 'w300')}
                        alt={member.name}
                        fill
                        sizes="112px"
                        className="object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <Users className="h-6 w-6 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="mt-2 px-0.5">
                    <p className="text-sm font-medium text-white leading-tight line-clamp-2 group-hover:text-red-400 transition-colors">{member.name}</p>
                    <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{member.character}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Watchlist meta (added / last watched) — quiet footer info */}
        {(item.addedAt || item.lastWatched) && (
          <div className="px-4 py-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            {item.addedAt && (
              <span>Added {format(new Date(item.addedAt), 'MMM d, yyyy')}</span>
            )}
            {item.lastWatched && (
              <span>Last watched {format(new Date(item.lastWatched), 'MMM d, yyyy')}</span>
            )}
          </div>
        )}
      </div>

      {/* Mobile swipe indicator - only show on mobile */}
      {isMobile && (canNavigatePrev || canNavigateNext) && (
        <div className="flex items-center justify-center px-4 py-2 border-t border-white/10">
          <span className="text-xs text-gray-500 flex items-center gap-2">
            {canNavigatePrev && <ChevronLeft className="h-3 w-3" />}
            Swipe to navigate
            {canNavigateNext && <ChevronRight className="h-3 w-3" />}
          </span>
        </div>
      )}

      {/* Rating Picker Modal */}
      {showRatingPicker && (
        <>
          <div
            className="absolute inset-0 bg-black/70 z-10"
            onClick={() => setShowRatingPicker(false)}
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
            <div
              className="bg-gray-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Rate {item.name}</h3>
                <button
                  onClick={() => setShowRatingPicker(false)}
                  className="p-1.5 hover:bg-gray-800 rounded-full"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div className="flex justify-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => {
                  const isFilled = (i + 1) <= ((hoverRating || item.personalRank || 0) / 2);
                  return (
                    <button
                      key={i}
                      onClick={() => handleStarClick(i)}
                      onMouseEnter={() => setHoverRating((i + 1) * 2)}
                      onMouseLeave={() => setHoverRating(null)}
                      className="transition-transform hover:scale-125"
                    >
                      <Star className={`h-10 w-10 ${isFilled ? 'fill-yellow-500 text-yellow-500' : 'fill-gray-700 text-gray-700'}`} />
                    </button>
                  );
                })}
              </div>

              <div className="text-center text-2xl font-bold text-yellow-500 mb-4">
                {(hoverRating || item.personalRank || 0) ? `${(hoverRating || item.personalRank || 0) / 2} / 5` : 'Tap to rate'}
              </div>

              {item.personalRank && (
                <button
                  onClick={async () => {
                    if (user) {
                      await ratingsService.removeUserRating(user.uid, item.id, item.type);
                      await setPersonalRank(item.id, 0);
                    }
                    setShowRatingPicker(false);
                  }}
                  className="w-full py-2 rounded-xl bg-red-900/30 text-red-400 text-sm hover:bg-red-900/50"
                >
                  Remove Rating
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );

  // Mobile layout (bottom sheet)
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/80 z-50 animate-in fade-in duration-200"
          onClick={onClose}
        />

        {/* Bottom sheet */}
        <div
          key={itemId}
          className={`fixed inset-x-0 bottom-0 z-50 ${
            !slideDirection && !lastNavDirection
              ? 'animate-in slide-in-from-bottom duration-300'
              : !slideDirection && lastNavDirection === 'left'
              ? 'animate-in slide-in-from-right duration-200'
              : !slideDirection && lastNavDirection === 'right'
              ? 'animate-in slide-in-from-left duration-200'
              : ''
          }`}
          style={{
            transform: `translateY(${dragOffset}px) translateX(${
              slideDirection === 'left'
                ? '-100%'
                : slideDirection === 'right'
                ? '100%'
                : `${swipeOffset}px`
            })`,
            opacity: slideDirection ? 0.5 : 1,
            transition: isDragging || isSwiping ? 'none' : 'transform 0.2s ease-out, opacity 0.2s ease-out'
          }}
          onTouchStart={isMobile ? handleTouchStart : undefined}
          onTouchMove={isMobile ? handleTouchMove : undefined}
          onTouchEnd={isMobile ? handleTouchEnd : undefined}
        >
          <div className="bg-black rounded-t-3xl max-h-[90vh] flex flex-col overflow-hidden relative">
            {/* Drag handle — absolute overlay so the hero backdrop extends behind it */}
            <div className="absolute top-0 inset-x-0 z-30 flex justify-center pt-2 pointer-events-none">
              <div className="w-12 h-1.5 bg-white/40 rounded-full" />
            </div>

            {/* More options menu (top right) */}
            <div className="absolute top-3 right-3 z-30">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
                className="rounded-full bg-black/60 ring-1 ring-white/20 backdrop-blur-md flex items-center justify-center hover:bg-black/80 transition-all"
              >
                <MoreVertical className="h-5 w-5 text-white" />
              </button>

              {showMoreMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMoreMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-1 min-w-[180px]">
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        onClose();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Close
                    </button>
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        if (confirm('Remove from watchlist?')) {
                          removeFromWatchlist(item.id);
                          onClose();
                        }
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove from Watchlist
                    </button>
                  </div>
                </>
              )}
            </div>

            {renderContent()}
          </div>
        </div>
      </>
    );
  }

  // Desktop layout (centered modal)
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        {/* Navigation arrows */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAnimatedPrev();
          }}
          disabled={!canNavigatePrev || isAnimating}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-r from-red-600 to-red-700 flex items-center justify-center text-white shadow-lg shadow-red-500/30 hover:from-red-500 hover:to-red-600 hover:shadow-xl hover:shadow-red-500/40 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none transition-all duration-200 z-10 ring-2 ring-white/20"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAnimatedNext();
          }}
          disabled={!canNavigateNext || isAnimating}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-r from-red-600 to-red-700 flex items-center justify-center text-white shadow-lg shadow-red-500/30 hover:from-red-500 hover:to-red-600 hover:shadow-xl hover:shadow-red-500/40 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none transition-all duration-200 z-10 ring-2 ring-white/20"
        >
          <ChevronRight className="h-7 w-7" />
        </button>

        {/* Dialog */}
        <div
          key={itemId}
          className={`bg-gradient-to-b from-gray-900 to-black rounded-3xl max-h-[90vh] max-w-3xl w-full flex flex-col overflow-hidden shadow-2xl relative transition-all duration-200 ease-out ${
            slideDirection === 'left'
              ? '-translate-x-full opacity-0 scale-95'
              : slideDirection === 'right'
              ? 'translate-x-full opacity-0 scale-95'
              : lastNavDirection === 'left'
              ? 'translate-x-0 opacity-100 scale-100 animate-in slide-in-from-right duration-200'
              : lastNavDirection === 'right'
              ? 'translate-x-0 opacity-100 scale-100 animate-in slide-in-from-left duration-200'
              : 'translate-x-0 opacity-100 scale-100 animate-in zoom-in-95 duration-200'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* More options menu (top right) */}
          <div className="absolute top-4 right-4 z-30">
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <MoreVertical className="h-5 w-5 text-white" />
            </button>

            {showMoreMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMoreMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-50 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-1 min-w-[180px]">
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      onClose();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      if (confirm('Remove from watchlist?')) {
                        removeFromWatchlist(item.id);
                        onClose();
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove from Watchlist
                  </button>
                </div>
              </>
            )}
          </div>

          {renderContent()}
        </div>
      </div>
    </>
  );
}
